// validators/inputCharacterValidator.ts
import { Rule } from "antd/es/form";

/**
 * Validation result structure (for internal use & error messages)
 */
interface ValidationCheck {
	isValid: boolean;
	hasCorruption: boolean;
	hasDisallowedChars: boolean;
	invalidChars: string[];
}

/**
 * Checks if input contains only allowed characters + detects mojibake corruption (Â)
 */
export const validateInputCharacters = (value: string | null | undefined): ValidationCheck => {
	if (!value || value.trim() === "") {
		return {
			isValid: true,
			hasCorruption: false,
			hasDisallowedChars: false,
			invalidChars: []
		};
	}

	// Step 1: Normalize (same as C# FormC)
	const normalized = value.normalize("NFC");

	// Step 2: Detect mojibake corruption - Â character
	const hasCorruption = normalized.includes("\u00C2");

	// Step 3: Allowed characters regex
	// Letters, numbers, whitespace, and safe punctuation
	//   const SAFE_CHARS_REGEX = /[^\p{L}\p{N}\s.,\-/#''&()@+_ :?= %~!*]/gu;
	const SAFE_CHARS_REGEX = /[^a-zA-Z0-9\s.,\-/#'&()@+_:?=%~!*]/g;

	// Find all disallowed characters
	const matches = normalized.match(SAFE_CHARS_REGEX) || [];

	// Remove Â from the disallowed list (we report it separately)
	const invalidChars = Array.from(new Set(matches)).filter((c) => c !== "\u00C2");

	const hasDisallowedChars = invalidChars.length > 0;

	return {
		isValid: !hasCorruption && !hasDisallowedChars,
		hasCorruption,
		hasDisallowedChars,
		invalidChars
	};
};

export const createCharacterValidatorRule = (): Rule => ({
	validator: (_, value: string) => {
		const result = validateInputCharacters(value);

		if (result.isValid) {
			return Promise.resolve();
		}

		// Build meaningful error message
		if (result.hasCorruption) {
			return Promise.reject(
				new Error(`Invalid character detected (possible copy-paste encoding issue). Please retype the text.`)
			);
		}

		if (result.hasDisallowedChars) {
			const chars = result.invalidChars.map((c) => `'${c}'`).join(", ");
			return Promise.reject(
				new Error(
					`Invalid character(s) ${chars}. Only letters, numbers, spaces and , . - / # ' & ( ) @ + _ : ? = % ~ ! * are allowed.`
				)
			);
		}

		// Fallback (shouldn't reach here)
		return Promise.reject(new Error(`Invalid format}`));
	}
});
